import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, groupBySection } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Prohibit, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import { Pagination, usePagination } from "@/components/common/Pagination";

export default function PatientsPage() {
  const { user } = useAuth();
  const canManage = !!user && (user.role === "admin" || user.role === "superuser" || user.can_delete_patients === true);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cf, setCf] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "", age: "", gender: "Male", phone: "", address: "", dob: "",
    blood_group: "", emergency_contact: "", allergies: "", custom_fields: {},
  });

  // Auto-open the register dialog when arriving via /patients?register=1
  useEffect(() => {
    if (searchParams.get("register") === "1") {
      setOpen(true);
      // strip the query so refresh/back behaves normally
      const next = new URLSearchParams(searchParams);
      next.delete("register");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Debounce the search term so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    api.get("/patients", {
      params: {
        q: debouncedQ || undefined,
        include_inactive: showInactive ? true : undefined,
      },
    }).then((r) => setPatients(r.data || []));
  }, [debouncedQ, showInactive]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/custom-fields", { params: { entity: "patient" } }).then((r) => setCf(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, age: form.age ? Number(form.age) : null };
      await api.post("/patients", payload);
      toast.success("Patient registered with new MRN");
      setOpen(false);
      setForm({ name: "", age: "", gender: "Male", phone: "", address: "", dob: "", blood_group: "", emergency_contact: "", allergies: "", custom_fields: {} });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const deactivatePatient = async (mrn, name) => {
    if (!window.confirm(`Deactivate "${name}" (${mrn})?\n\nThe patient and all their records (OPD/IPD/bills) stay in the system but are hidden from default lists. You can reactivate any time. Permanent delete is only allowed after deactivation.`)) return;
    try {
      await api.patch(`/patients/${mrn}/deactivate`);
      toast.success(`Deactivated ${mrn}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to deactivate");
    }
  };

  const reactivatePatient = async (mrn) => {
    try {
      await api.patch(`/patients/${mrn}/activate`);
      toast.success(`Reactivated ${mrn}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const deletePatient = async (mrn, name) => {
    const confirmText = `DELETE ${mrn}`;
    const reply = window.prompt(
      `PERMANENT delete of "${name}" (${mrn}) and ALL related data:\n` +
      `• OPD visits  • IPD admissions  • Progress notes\n` +
      `• Bills & payment receipts  • Discharge summary\n` +
      `Any occupied bed will be freed. This cannot be undone.\n\n` +
      `Type "${confirmText}" to confirm.`,
    );
    if (reply !== confirmText) {
      if (reply !== null) toast.error("Confirmation text did not match — patient was NOT deleted");
      return;
    }
    try {
      const { data } = await api.delete(`/patients/${mrn}`);
      const d = data.deleted || {};
      toast.success(
        `Deleted: 1 patient · ${d.opd_visits || 0} OPD · ${d.admissions || 0} admissions · ` +
        `${d.bills || 0} bills · ${d.progress_notes || 0} progress notes · ${d.freed_beds || 0} beds freed`,
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  // Pagination over the (already-filtered-by-q) patients list.
  const { page, setPage, pageSize, setPageSize, paged, total, pageCount } = usePagination(patients, 25);

  return (
    <div data-testid="patients-page">
      <PageHeader
        eyebrow="Master register"
        title="Patients"
        subtitle="MRN is auto-generated per hospital. Repeat patients keep the same MRN — start typing a name in the register dialog to reuse an existing record."
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--hms-muted)]" />
              <Input
                placeholder="Search MRN, name, mobile…"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="pl-8 w-64 rounded-sm border-[var(--hms-border-2)] bg-white"
                data-testid="patients-search"
              />
            </div>
            {canManage && (
              <label className="flex items-center gap-2 text-xs font-medium text-[var(--hms-text-2)] select-none">
                <Switch
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  data-testid="toggle-show-inactive"
                />
                Show inactive
              </label>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="register-patient-btn" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
                  <Plus size={14} weight="bold" className="mr-1.5" /> Register patient
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-2xl">
                <DialogHeader><DialogTitle className="font-heading">Register a new patient</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="register-patient-form">
                  <div className="col-span-3">
                    <NameWithSuggest
                      value={form.name}
                      onChange={(v) => setForm({ ...form, name: v })}
                    />
                    <p className="text-[10px] text-[var(--hms-muted)] mt-1">
                      Tip: if this patient already exists, pick the suggestion — same MRN will be reused, only a new visit / admission is created.
                    </p>
                  </div>
                  <PField label="Age" required type="number" value={form.age} onChange={(v) => setForm({ ...form, age: v })} testid="p-age" />
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Gender</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger data-testid="p-gender" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <PField label="Blood group" value={form.blood_group} onChange={(v) => setForm({ ...form, blood_group: v })} testid="p-blood" />
                  <PField label="Mobile" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="p-phone" />
                  <PField label="DOB" type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testid="p-dob" />
                  <PField label="Emergency contact" value={form.emergency_contact} onChange={(v) => setForm({ ...form, emergency_contact: v })} testid="p-em" />
                  <div className="col-span-3">
                    <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Address</Label>
                    <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="p-addr" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Allergies / Notes</Label>
                    <Textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="p-allergies" />
                  </div>
                  {cf.length > 0 && (
                    <div className="col-span-3 border-t border-[var(--hms-border)] pt-3 mt-1 space-y-3">
                      {Object.entries(groupBySection(cf)).map(([section, fields]) => (
                        <div key={section}>
                          <div className="hms-section-title mb-2">{section}</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {fields.map((f) => (
                              <CustomInput key={f.id} field={f}
                                value={form.custom_fields[f.key]}
                                onChange={(v) => setForm({ ...form, custom_fields: { ...form.custom_fields, [f.key]: v } })}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <DialogFooter className="col-span-3 mt-2">
                    <Button type="submit" disabled={busy} data-testid="p-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
                      {busy ? "Saving…" : "Register & assign MRN"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      {patients.length === 0 ? (
        <EmptyState title="No patients yet" hint="Register your first patient — MRN will be assigned automatically." />
      ) : (
        <div className="hms-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
              <tr>
                <th className="text-left px-4 py-2.5">MRN</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Age/Sex</th>
                <th className="text-left px-4 py-2.5">Phone</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Registered</th>
                {canManage && <th className="text-right px-4 py-2.5">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => {
                const isInactive = p.active === false;
                return (
                  <tr key={p.id} className={`border-b border-[var(--hms-border)] hover:bg-[var(--hms-paper)]/50 ${isInactive ? "opacity-60" : ""}`}>
                    <td className="px-4 py-2.5 font-mono text-xs">
                      <Link to={`/patients/${p.mrn}`} data-testid={`patient-link-${p.mrn}`} className="hover:text-[var(--hms-accent)] font-semibold">
                        {p.mrn}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 font-medium">
                      {p.name}
                      {isInactive && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-[var(--hms-muted)]">· inactive</span>}
                    </td>
                    <td className="px-4 py-2.5">{p.age || "—"} / {p.gender || "—"}</td>
                    <td className="px-4 py-2.5">{p.phone || "—"}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-2.5 hms-tabular text-[var(--hms-text-2)]">{fmtDate(p.created_at)}</td>
                    {canManage && (
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {isInactive ? (
                          <>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => reactivatePatient(p.mrn)}
                              className="text-[var(--hms-sidebar)] rounded-sm"
                              title="Reactivate patient"
                              data-testid={`patient-reactivate-${p.mrn}`}
                            >
                              <ArrowCounterClockwise size={14} weight="bold" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => deletePatient(p.mrn, p.name)}
                              className="text-[var(--hms-accent)] rounded-sm"
                              title="Permanently delete (cascades to all records)"
                              data-testid={`patient-del-${p.mrn}`}
                            >
                              <Trash size={14} weight="bold" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => deactivatePatient(p.mrn, p.name)}
                            className="text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] rounded-sm"
                            title="Deactivate patient (records are kept)"
                            data-testid={`patient-deactivate-${p.mrn}`}
                          >
                            <Prohibit size={14} weight="bold" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={page} setPage={setPage} pageSize={pageSize}
            setPageSize={setPageSize} total={total} pageCount={pageCount} testidPrefix="patients" />
        </div>
      )}
    </div>
  );
}

function NameWithSuggest({ value, onChange }) {
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [picked, setPicked] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  const q = useMemo(() => (value || "").trim(), [value]);

  useEffect(() => {
    if (picked) return; // user already chose a suggestion; don't re-fire
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) {
      setMatches([]); setOpenList(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      api.get("/patients", { params: { q, include_inactive: true } })
        .then((r) => {
          setMatches((r.data || []).slice(0, 6));
          setOpenList(true);
        }).catch(() => setMatches([]));
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, picked]);

  // Close on outside click
  useEffect(() => {
    const h = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpenList(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleChange = (v) => {
    setPicked(false);
    onChange(v);
  };

  return (
    <div ref={boxRef} className="relative">
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Full name *</Label>
      <Input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => { if (matches.length > 0) setOpenList(true); }}
        required
        autoComplete="off"
        data-testid="p-name"
        placeholder="Start typing — existing patients will appear"
        className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
      />
      {openList && matches.length > 0 && (
        <div
          data-testid="patient-suggest-list"
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[var(--hms-border-2)] rounded-sm shadow-lg max-h-72 overflow-auto"
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-[var(--hms-muted)] bg-[var(--hms-paper)] border-b border-[var(--hms-border)]">
            Existing patients — click to reuse MRN
          </div>
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`patient-suggest-${p.mrn}`}
              onClick={() => {
                setPicked(true);
                setOpenList(false);
                nav(`/patients/${p.mrn}`);
              }}
              className="w-full text-left px-3 py-2 hover:bg-[var(--hms-paper)] border-b border-[var(--hms-border)] last:border-0"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium text-sm">
                  {p.name}
                  {p.active === false && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-[var(--hms-muted)]">· inactive</span>}
                </div>
                <div className="font-mono text-[11px] text-[var(--hms-accent)]">{p.mrn}</div>
              </div>
              <div className="text-[11px] text-[var(--hms-text-2)] mt-0.5">
                {p.age || "—"} / {p.gender || "—"} · {p.phone || "no phone"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PField({ label, value, onChange, required, type, testid, colSpan }) {
  return (
    <div className={colSpan === 3 ? "col-span-3" : colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}{required && " *"}</Label>
      <Input type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
    </div>
  );
}

export function CustomInput({ field, value, onChange }) {
  const common = { value: value ?? "", onChange: (e) => onChange(e.target.value), required: field.required, "data-testid": `cf-${field.key}` };
  if (field.type === "textarea") {
    return (
      <div>
        <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{field.label}{field.required && " *"}</Label>
        <Textarea {...common} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{field.label}{field.required && " *"}</Label>
        <Select value={value || ""} onValueChange={(v) => onChange(v)}>
          <SelectTrigger data-testid={`cf-${field.key}`} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  const t = field.type === "number" ? "number" : field.type === "date" ? "date" : "text";
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{field.label}{field.required && " *"}</Label>
      <Input type={t} {...common} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
    </div>
  );
}
